/**
 * CHAT-001 — in-platform user chat client (2026-06-11, Shane).
 * Mirrors apps/api/src/routes/chat. Chats are private between
 * participants; delivery is short-polling (no websockets in v1).
 */
import { apiFetch } from './api';

export interface ChatDirectoryEntry {
  userId: string;
  name: string;
  lastSeen: string | null;
}

export interface ChatMessagePublic {
  id: string;
  conversationId: string;
  senderUserId: string;
  senderName: string;
  body: string;
  createdAt: string;
}

export interface ChatConversationPublic {
  id: string;
  isGroup: boolean;
  title: string | null;
  participants: Array<{ userId: string; name: string }>;
  lastMessage: { body: string; senderName: string; createdAt: string } | null;
  unreadCount: number;
}

export async function fetchChatDirectory(signal?: AbortSignal): Promise<ChatDirectoryEntry[]> {
  return apiFetch<ChatDirectoryEntry[]>('/api/chat/directory', { method: 'GET', signal });
}

export async function fetchConversations(
  signal?: AbortSignal,
): Promise<ChatConversationPublic[]> {
  return apiFetch<ChatConversationPublic[]>('/api/chat/conversations', {
    method: 'GET',
    signal,
  });
}

export async function createChatConversation(input: {
  userIds: string[];
  title?: string;
}): Promise<{ conversationId: string; isGroup: boolean; reused: boolean }> {
  return apiFetch<{ conversationId: string; isGroup: boolean; reused: boolean }>(
    '/api/chat/conversations',
    { method: 'POST', body: input },
  );
}

export async function fetchChatMessages(
  conversationId: string,
  since?: string | null,
  signal?: AbortSignal,
): Promise<ChatMessagePublic[]> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : '';
  return apiFetch<ChatMessagePublic[]>(
    `/api/chat/conversations/${conversationId}/messages${qs}`,
    { method: 'GET', signal },
  );
}

export async function sendChatMessage(
  conversationId: string,
  body: string,
): Promise<ChatMessagePublic> {
  return apiFetch<ChatMessagePublic>(
    `/api/chat/conversations/${conversationId}/messages`,
    { method: 'POST', body: { body } },
  );
}

export async function markChatRead(conversationId: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/chat/conversations/${conversationId}/read`, {
    method: 'POST',
  });
}

/**
 * DIRECTOR INVESTIGATION EXPORT (wired 2026-08-16).
 *
 * The endpoint has existed since CHAT-001 and nothing called it —
 * IMPORT-EXPORT-AUDIT-001 FAULT 3. The widget's own header names it
 * ("the only other read path is the director investigation-export (loudly
 * audited)"), so the design always intended it; no control ever offered it.
 *
 * The server requires a written reason of at least 10 characters and writes
 * `chat.investigation_export` to the audit log with that reason BEFORE
 * returning anything. That ordering matters: the record of the read exists
 * even if the caller never uses what comes back.
 */
export interface ChatInvestigationExport {
  messages: ChatMessagePublic[];
  participants: string[];
}

export async function investigationExportConversation(
  conversationId: string,
  reason: string,
): Promise<ChatInvestigationExport> {
  return apiFetch<ChatInvestigationExport>(
    `/api/chat/conversations/${encodeURIComponent(conversationId)}/investigation-export`,
    { method: 'POST', body: { reason } },
  );
}
